import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Compass, Users, TrendingUp, Calendar, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { stats, programs } from '../mockData';

const API = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/events?status=active`)
      .then(r => r.json())
      .then(data => {
        const allEvents = (data.events || [])
          .filter(e => new Date(e.date) >= new Date(new Date().toDateString()))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        // Deduplicate recurring events: show only the next occurrence per title
        const seen = new Set();
        const deduped = allEvents.filter(e => {
          if (seen.has(e.title)) return false;
          seen.add(e.title);
          return true;
        });
        setUpcomingEvents(deduped.slice(0, 3));
      })
      .catch(() => {});
  }, []);
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#E6B933]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gray-200/50 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-block mb-6 px-6 py-2 bg-[#E6B933]/10 rounded-full border border-[#E6B933]/20">
            <p className="text-[#E6B933] font-semibold text-sm">Empowering Youth • Building Futures</p>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            Wisdom Helping Youth
            <br />
            <span className="text-[#E6B933]">Maximize Everything</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
            The WHY ME Foundation empowers underprivileged youth in Northwest Houston through comprehensive programs in life skills, academic guidance, and career development.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-involved">
              <Button className="bg-[#E6B933] hover:bg-[#D4A01F] text-white font-semibold px-8 py-6 text-lg group transition-all duration-300 hover:scale-105">
                Support Our Mission
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={20} />
              </Button>
            </Link>
            <Link to="/programs">
              <Button variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-black hover:text-white font-semibold px-8 py-6 text-lg transition-all duration-300">
                Explore Programs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="py-20 bg-white" data-testid="home-upcoming-events">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Upcoming <span className="text-[#E6B933]">Events</span>
              </h2>
              <p className="text-lg text-gray-600">Join us at our next community gathering</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {upcomingEvents.map(event => {
                const eventDate = new Date(event.date);
                return (
                  <Card key={event._id} className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#E6B933]" data-testid={`home-event-${event._id}`}>
                    {event.image_url && (
                      <div className="h-40 overflow-hidden">
                        <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase mb-3 inline-block ${
                        event.event_type === 'volunteer' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>{event.event_type}</span>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h3>
                      <div className="space-y-1.5 text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#E6B933]" />{eventDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#E6B933]" />{event.time}</div>
                        <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#E6B933]" />{event.location}</div>
                      </div>
                      <Link to="/events" className="text-[#E6B933] font-semibold inline-flex items-center hover:gap-2 transition-all duration-300">
                        Register <ArrowRight className="ml-1" size={18} />
                      </Link>
                    </div>
                  </Card>
                );
              })}
            </div>
            <div className="text-center mt-10">
              <Link to="/events">
                <Button variant="outline" className="border-2 border-[#E6B933] text-[#E6B933] hover:bg-[#E6B933] hover:text-white font-semibold px-8 py-3 transition-all duration-300">
                  View All Events <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Stats Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="text-4xl md:text-5xl font-bold text-[#E6B933] mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.number}
                </div>
                <div className="text-gray-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Impact - Community Photos */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-[#E6B933]">Growing Community</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Making a positive impact through youth programs, community service, and charitable giving
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_foundation-web-5/artifacts/vkbu5f6n_IMG_4376.png" 
                alt="WHY ME Foundation Beau Strong Toy Drive at Texas Children's Hospital" 
                className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Beau Strong Toy Drive</h3>
                <p className="text-gray-600">Spreading joy to children and families at Texas Children's Hospital</p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_foundation-web-5/artifacts/85cfcz1c_IMG_4351.png" 
                alt="WHY ME Foundation youth members at community mural" 
                className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Youth Leadership in Action</h3>
                <p className="text-gray-600">Building confidence, character, and community connections</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Mission:
                <span className="block text-[#E6B933] mt-2">Influence, Inform, Impact</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We address the disproportionate barriers faced by youth in low-income communities by providing early exposure to life skills, academic guidance, entrepreneurship, and modern wealth-building pathways.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                In areas like Northwest Houston and Greenspoint, where over 75% of students are considered "At-Risk," we provide the intellectual, physical, and creative tools required to thrive.
              </p>
              <Link to="/about">
                <Button className="bg-black hover:bg-gray-800 text-white font-semibold px-6 py-3 group transition-all duration-300">
                  Learn More About Us
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={18} />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_why-me-org/artifacts/8z4wu2lr_WHYME%20BKFST.jpg" 
                alt="Community breakfast program" 
                className="rounded-lg shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_why-me-org/artifacts/mdkmyo0b_RUNWME.jpg" 
                alt="Running program" 
                className="rounded-lg shadow-lg w-full h-64 object-cover mt-8 hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Programs Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Three-Pillar Framework</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive programs designed to prepare, guide, and elevate youth for long-term success
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program, index) => {
              const IconComponent = program.icon === 'Target' ? Target : program.icon === 'Compass' ? Compass : Users;
              return (
                <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#E6B933] group">
                  <div className="w-16 h-16 bg-[#E6B933]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#E6B933] transition-colors duration-300">
                    <IconComponent className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{program.title}</h3>
                  <p className="text-[#E6B933] font-semibold mb-4">{program.subtitle}</p>
                  <p className="text-gray-600 mb-6">{program.description}</p>
                  <Link to="/programs" className="text-[#E6B933] font-semibold inline-flex items-center group-hover:gap-2 transition-all duration-300">
                    Learn More
                    <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform duration-300" size={18} />
                  </Link>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-black to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">Be Part of the Change</h2>
          <p className="text-xl text-gray-300 mb-8">
            Your support helps us expand opportunities for underprivileged youth and build stronger communities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-involved">
              <Button className="bg-[#E6B933] hover:bg-[#D4A01F] text-white font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                Donate Now
              </Button>
            </Link>
            <Link to="/get-involved">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-semibold px-8 py-6 text-lg transition-all duration-300">
                Become a Volunteer
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
